import { getFunctions, httpsCallable, type HttpsCallableOptions } from 'firebase/functions';
import { app } from './firebase';

const functions = getFunctions(app, 'us-central1');

export type MintedBy = 'web' | 'ios';

export interface WireCalendarEvent {
  externalId: string;
  title: string;
  startMs: number;
  endMs: number;
  isAllDay: boolean;
  /** All-day events only: bare calendar dates ("YYYY-MM-DD", end exclusive).
   *  Resolve to LOCAL midnight — startMs/endMs are UTC-midnight fallbacks. */
  startDate?: string;
  endDate?: string;
  location?: string;
  color?: string;
  meetingUrl?: string;
}

export interface ReconcileCalendarOutput {
  events: WireCalendarEvent[];
  syncedAtMs: number;
}

export interface ProbeCalendarOutput {
  ok: boolean;
  reason?: 'rate-limit' | 'token-expired' | 'api-failure';
}

const opts: HttpsCallableOptions = { limitedUseAppCheckTokens: false };

export const callStoreGoogleRefreshToken = async (
  refreshToken: string,
  mintedBy: MintedBy,
): Promise<{ ok: true }> =>
  (
    await httpsCallable<{ refreshToken: string; mintedBy: MintedBy }, { ok: true }>(
      functions,
      'storeGoogleRefreshToken',
      opts,
    )({ refreshToken, mintedBy })
  ).data;

export interface ExchangeGoogleCodeInput {
  code: string;
  /** Web GIS popup flow sends 'postmessage'. iOS native flow omits or sends ''
   * — Google's token endpoint accepts the exchange without it when the code
   * came from an iOS native client. */
  redirectUri?: string;
}

export interface ExchangeGoogleCodeOutput {
  accessToken: string;
  expiresInSec: number;
  email?: string;
}

export const callExchangeGoogleCode = async (
  input: ExchangeGoogleCodeInput,
): Promise<ExchangeGoogleCodeOutput> =>
  (
    await httpsCallable<ExchangeGoogleCodeInput, ExchangeGoogleCodeOutput>(
      functions,
      'exchangeGoogleCode',
      opts,
    )(input)
  ).data;

export const callReconcileGoogleCalendar = async (input?: {
  rangeStartMs?: number;
  rangeEndMs?: number;
}): Promise<ReconcileCalendarOutput> =>
  (
    await httpsCallable<typeof input, ReconcileCalendarOutput>(
      functions,
      'reconcileGoogleCalendar',
      opts,
    )(input ?? {})
  ).data;

export const callProbeGoogleCalendar = async (): Promise<ProbeCalendarOutput> =>
  (
    await httpsCallable<Record<string, never>, ProbeCalendarOutput>(
      functions,
      'probeGoogleCalendar',
      opts,
    )({})
  ).data;

export const callRevokeGoogleRefreshToken = async (): Promise<{ ok: true }> =>
  (
    await httpsCallable<Record<string, never>, { ok: true }>(
      functions,
      'revokeGoogleRefreshToken',
      opts,
    )({})
  ).data;
